// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useEffect, useState } from 'react';
import {
  Fade,
  CircularProgress,
  TextField,
  Typography,
  Button,
  Dialog, DialogActions, DialogContent,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import HelpOutlinedIcon from '@mui/icons-material/HelpOutlined';
import { Box, useTheme } from '@mui/system';
import { mininumBufferSize, defaultBufferSize, minimumRoadNetworkBufferSize, defaultRoadNetworkBufferSize, mininumBufferSizeOverride, RETRIEVE_ALL_ROADS } from 'Utils/constants';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { GenerateKmlBufferThunk, SetLineStringUploadThunk, SetRoadNetworkThunk, GenerateRoadNetworkKmlBufferThunk } from 'Features/kml/kmlThunk';
import { useSelector } from 'react-redux';
import { getOrders } from 'Features/order/orderSelectors';
import { RootState } from 'Store';
import { SetBufferSizeAction, SetSelectedRoadTypesAction } from 'Features/kml/kmlActions';
import { useFlags } from 'launchdarkly-react-client-sdk';
import stylesDef from './styles';
import LoadingSpinner from 'Components/LoadingSpinner';

// Keeping this constant here and not in constant file as it is specific this component only
const EXCLUDED_DEFAULT_ROAD_TYPES = ['unclassified', 'trunk', RETRIEVE_ALL_ROADS];

export const KmlBufferDialog = () => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();

  const { activeOrder } = useSelector((state: RootState) => getOrders(state));
  const geomType = activeOrder?.geomType;

  const lineStringKmlUpload = useAppSelector((state) => state.kml.present.lineStringKmlUpload);
  const getRoadNetwork = useAppSelector((state) => state.kml.present.getRoadNetwork);
  const { availableRoadTypes, roadTypesLoading } = useAppSelector((state) => state.kml.present);
  const creatingBuffer = useAppSelector((state) => state.kml.present.creatingBuffer);
  const creatingRoadNetworkBuffer = useAppSelector((state) => state.kml.present.creatingRoadNetworkBuffer);
  const projectId = useAppSelector((state) => state.project.projectId);

  const roadNetworkBuffer = geomType === 'Polygon';

  const { minBufferSizeOverride } = useFlags();

  const defaultBuffer = roadNetworkBuffer ? defaultRoadNetworkBufferSize : defaultBufferSize;
  const minBuffer = minBufferSizeOverride ? mininumBufferSizeOverride : (roadNetworkBuffer ? minimumRoadNetworkBufferSize : mininumBufferSize);
  const [buffer, setBuffer] = useState(minBuffer);
  const [selectedRoadTypes, setSelectedRoadTypes] = useState<string[]>([]);

  useEffect(() => {
    const defaultRoadTypes = availableRoadTypes.filter((type: string) => !EXCLUDED_DEFAULT_ROAD_TYPES.includes(type));
    setSelectedRoadTypes(defaultRoadTypes);
  }, [availableRoadTypes]);

  useEffect(() => {
    setBuffer(minBuffer);
  }, [minBuffer]);

  const onBufferSubmit = () => {
    let finalBufferSize = defaultBuffer;
    if (!Number.isNaN(buffer)) {
      finalBufferSize = Math.max(buffer, minBuffer);
    }

    setBuffer(finalBufferSize);
    dispatch(SetBufferSizeAction(finalBufferSize));
    dispatch(SetSelectedRoadTypesAction(selectedRoadTypes));
    if (roadNetworkBuffer) {
      window?.pendo?.track('Road Network KML buffer created', { buffer_distance: finalBufferSize?.toFixed(2), selectedRoadTypes, projectId });
      dispatch(GenerateRoadNetworkKmlBufferThunk());
    } else {
      window?.pendo?.track('KML buffer created', { buffer_distance: finalBufferSize?.toFixed(2), projectId });
      dispatch(GenerateKmlBufferThunk());
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBuffer(parseFloat(e.target.value));
  };

  const handleBlur = () => {
    if (buffer < minBuffer) {
      setBuffer(minBuffer);
    }
  };

  const handleRoadTypeCheck = (event: any, value: string) => {
    if (event.target.checked) {
      setSelectedRoadTypes((prev) => [...prev, value]);
    } else {
      setSelectedRoadTypes((prev) => prev.filter((val) => val !== value));
    }
  };

  return (
    <Dialog
      open={lineStringKmlUpload || getRoadNetwork}
      onClose={() => { dispatch(SetLineStringUploadThunk(false)); }}
      sx={styles.dialog}
    >
      <Box sx={[styles.dialogContent, (availableRoadTypes.length === 0 && !roadTypesLoading) && styles.dialogContentEmpty]}>
        <Typography sx={styles.dialogHeader} variant="h5">
          {lineStringKmlUpload ? 'Buffer Distance' : 'Get Road Network'}
        </Typography>
        <DialogContent sx={styles.dialogText}>
          {lineStringKmlUpload && (
            <Typography>
              Please add below the buffer distance for your Right of Way. Please note AirWorks requires a minimum buffer distance of
              {` ${minBuffer} feet.`} AirWorks recommends a {minBuffer} foot buffer on either side of the input segment.
            </Typography>
          )}
          {!lineStringKmlUpload && (
            <Box>
              <Typography>
                You can generate a line-string KML file representing roads in your chosen area. All available road types for this area are listed here. If none are listed, OSM does not have a road network to render.
                {' '}
                Road Network data made available by
                {' '}
                <a style={styles.link} href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a>
                {' '}
                under the Open Database License.
              </Typography>
            </Box>
          )}
          <Box sx={styles.textForm}>
            <TextField
              id="buffer"
              label="Buffer Distance"
              variant="outlined"
              type="number"
              onChange={handleChange}
              onBlur={handleBlur}
              inputProps={{ min: minBuffer }}
              value={buffer.toString()}
              sx={styles.textField}
            />
            <Box>
              <Typography variant="h5">ft</Typography>
            </Box>
          </Box>
          {roadNetworkBuffer && (
            <LoadingSpinner loading={roadTypesLoading}>
              {availableRoadTypes?.length > 0 && (
                <>
                  <Box sx={styles.roadTypesTitle}>
                    <Typography variant="h5">Road Types</Typography>
                  </Box>
                  {availableRoadTypes.map((value: string) => (
                    <Box>
                      <FormControlLabel
                        key={value}
                        control={<Checkbox sx={styles.checked} />}
                        label={value[0].toUpperCase() + value.slice(1)}
                        onChange={((event) => handleRoadTypeCheck(event, value))}
                        checked={selectedRoadTypes.includes(value)}
                      />
                    </Box>
                  ))}
                </>
              )}
            </LoadingSpinner>
          )}
        </DialogContent>
        <DialogActions sx={styles.dialogActions}>
          <Button sx={styles.learnMoreButton} onClick={() => window.open('https://awkb.scrollhelp.site/airworksknowledgebase/extracting-road-network-data-from-openstreetmap-in', '_blank')}>
            <HelpOutlinedIcon fontSize='small' sx={styles.helpIcon} />
            Learn more
          </Button>
          <Button onClick={() => { lineStringKmlUpload ? dispatch(SetLineStringUploadThunk(false)) : dispatch(SetRoadNetworkThunk(false)) }}>Close</Button>
          {
            (!roadNetworkBuffer || (availableRoadTypes.length > 0 && roadNetworkBuffer)) && (
              <Button
                variant="contained"
                color="primary"
                onClick={onBufferSubmit}
                disabled={roadNetworkBuffer && selectedRoadTypes.length === 0 || creatingBuffer || creatingRoadNetworkBuffer}
              >
                <Fade in={creatingBuffer || creatingRoadNetworkBuffer}>
                  <CircularProgress size={24} sx={styles.buttonProgress} />
                </Fade>
                <Fade in={!creatingBuffer && !creatingRoadNetworkBuffer}>
                  <span>Continue</span>
                </Fade>
              </Button>
            )
          }
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default KmlBufferDialog;
